import React, { forwardRef, useState } from 'react';
import { cn } from '../../utils/cn';

export interface AnimatedInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  variant?: 'default' | 'filled' | 'outlined';
  size?: 'sm' | 'md' | 'lg';
  floatingLabel?: boolean;
  animate?: boolean;
}

const AnimatedInput = forwardRef<HTMLInputElement, AnimatedInputProps>(
  (
    {
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      variant = 'default',
      size = 'md',
      floatingLabel = true,
      animate = true,
      className,
      id,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [hasValue, setHasValue] = useState(!!props.value || !!props.defaultValue);

    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

    const baseClasses = 'form-input';

    const variantClasses = {
      default: 'bg-white border-2 border-neutral-300 focus:border-primary-500',
      filled: 'bg-neutral-100 border-2 border-transparent focus:border-primary-500 focus:bg-white',
      outlined: 'bg-transparent border-2 border-neutral-300 focus:border-primary-500',
    };

    const sizeClasses = {
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-5 py-4 text-lg',
    };

    const hasError = !!error;
    const isLabelFloating = floatingLabel && (isFocused || hasValue);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      setHasValue(!!e.target.value);
      props.onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setHasValue(!!e.target.value);
      props.onChange?.(e);
    };

    return (
      <div className="form-group">
        <div className="relative">
          {floatingLabel && label && (
            <label
              htmlFor={inputId}
              className={cn(
                'absolute left-3 transition-all duration-300 ease-spring pointer-events-none',
                size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-lg' : 'text-base',
                isLabelFloating
                  ? 'top-1 -translate-y-1/2 scale-90 text-primary-600 bg-white px-1'
                  : 'top-1/2 -translate-y-1/2 text-neutral-500',
                hasError && 'text-error-600',
              )}
            >
              {label}
            </label>
          )}

          {!floatingLabel && label && (
            <label htmlFor={inputId} className="form-label">
              {label}
            </label>
          )}

          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">
              {leftIcon}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            className={cn(
              baseClasses,
              variantClasses[variant],
              sizeClasses[size],
              {
                'pl-10': leftIcon,
                'pr-10': rightIcon,
                'border-error-500 focus:border-error-500 focus:ring-error-100': hasError,
                'border-neutral-300 focus:border-primary-500 focus:ring-primary-100': !hasError,
                'animate-pulse': animate && isFocused,
              },
              'w-full rounded-lg transition-all duration-300 ease-spring focus:outline-none focus:ring-4',
              className,
            )}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            {...props}
          />

          {rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">
              {rightIcon}
            </div>
          )}

          {/* Focus indicator */}
          {isFocused && animate && (
            <div className="absolute inset-0 rounded-lg ring-4 ring-primary-100 pointer-events-none animate-pulse" />
          )}
        </div>

        {error && (
          <div className="mt-2 flex items-center gap-2 text-sm text-error-600 animate-slide-in-top">
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {helperText && !error && (
          <p className="mt-2 text-sm text-neutral-500 animate-fade-in">{helperText}</p>
        )}
      </div>
    );
  },
);

AnimatedInput.displayName = 'AnimatedInput';

export { AnimatedInput };
